/**
 * Recurrence expansion — the RRULE subset (Phase 4, PLAN.recurrence §2):
 * FREQ=DAILY/WEEKLY/MONTHLY, INTERVAL, COUNT xor UNTIL, BYDAY (weekly only).
 * Pure local-wall-clock iteration over calendarDates' calendar arithmetic —
 * no date library, and 09:00 stays 09:00 across DST boundaries by
 * construction (occurrence day via addDays from the SERIES anchor, wall time
 * re-applied via dayAtMinutes).
 *
 * Decisions carried from the plan (each test-pinned):
 * - Occurrence identity = the LOCAL date key of the occurrence start
 *   (`occurrence_date`) — the subset yields at most one occurrence per day.
 * - Monthly day-31 SKIPS months lacking the day (RFC 5545 §3.3.10 "invalid
 *   dates are ignored") — never clamps to the 30th; a skipped invalid date is
 *   never generated, so it does not consume COUNT.
 * - COUNT counts GENERATED occurrences BEFORE exdate/override suppression
 *   (EXDATE removes instances from the computed set — suppressing one never
 *   extends the series). UNTIL is an inclusive local date key.
 * - Iteration is anchored at the series start, never at the window start —
 *   INTERVAL>1 series stay on their own grid however the window slices them.
 * - Malformed rules degrade to a single event with one console.warn — an
 *   expansion bug must never blank the calendar mid-render.
 */
import {
  addDays,
  dayAtMinutes,
  minutesOfDay,
  startOfWeek,
  startOfDay,
  toDateKey,
  fromDateKey,
  daysInMonth,
} from './calendarDates';

export const MAX_ITERATIONS = 1000;

const BYDAY_CODES = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
/** JS getDay() (0=Sun) for a BYDAY code. */
const DAY_INDEX = { MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6, SU: 0 };
/** Offset from a Monday week start for a BYDAY code. */
const MONDAY_OFFSET = { MO: 0, TU: 1, WE: 2, TH: 3, FR: 4, SA: 5, SU: 6 };

export function byDayCodeForDate(date) {
  return BYDAY_CODES.find((c) => DAY_INDEX[c] === date.getDay());
}

/** null when valid, else a reason string (malformed → single-event fallback). */
export function recurrenceProblem(recurrence) {
  if (recurrence == null) return null;
  if (!['daily', 'weekly', 'monthly'].includes(recurrence.freq)) {
    return `unknown freq "${recurrence.freq}"`;
  }
  const interval = recurrence.interval ?? 1;
  if (!Number.isInteger(interval) || interval < 1) return `bad interval ${recurrence.interval}`;
  if (recurrence.count != null && recurrence.until != null) return 'both count and until';
  if (recurrence.count != null && (!Number.isInteger(recurrence.count) || recurrence.count < 1)) {
    return `bad count ${recurrence.count}`;
  }
  if (recurrence.until != null && !fromDateKey(recurrence.until)) {
    return `bad until "${recurrence.until}"`;
  }
  if (recurrence.by_day != null) {
    if (recurrence.freq !== 'weekly') return 'by_day outside weekly';
    if (!Array.isArray(recurrence.by_day) || recurrence.by_day.length === 0) return 'empty by_day';
    for (const code of recurrence.by_day) {
      if (!BYDAY_CODES.includes(code)) return `bad by_day code "${code}"`;
    }
  }
  return null;
}

/** The master's series-anchor local start Date (midnight for all-day). */
function anchorStartOf(master) {
  if (master.all_day) return fromDateKey(master.start_date) || startOfDay(new Date());
  return new Date(master.start);
}

/**
 * Generate the LOCAL start-of-day Dates of every occurrence (pre-suppression),
 * capped by COUNT / UNTIL / `generateUntil` (exclusive) / MAX_ITERATIONS.
 * Chronological by construction.
 */
export function occurrenceDays(master, generateUntil, windowStart = null) {
  const rule = master.recurrence;
  const anchor = anchorStartOf(master);
  const anchorDay = startOfDay(anchor);
  const interval = rule.interval ?? 1;
  const untilDay = rule.until != null ? fromDateKey(rule.until) : null;
  const out = [];
  let steps = 0;
  let capTripped = false;

  // O(1) anchor FAST-FORWARD (tester fold-in): a window >MAX_ITERATIONS steps
  // past the anchor used to blank the series silently. For ENDLESS and UNTIL
  // rules the cursor jumps to just before the window (still ON the series
  // grid — index arithmetic, two grid steps of slack for DST/rounding).
  // COUNT rules deliberately stay on the bounded iterator: every generated
  // occurrence must be counted from the anchor, and a COUNT ≤ the cap ends
  // within it anyway (COUNT > cap keeps the pre-existing, test-pinned cap
  // behaviour — now with the warning below instead of silence).
  const fastForward = (unitsBetween) => {
    if (rule.count != null || windowStart == null) return 0;
    return Math.max(0, Math.floor(unitsBetween / interval) - 2);
  };
  const wsDay = windowStart != null ? startOfDay(windowStart) : null;

  const accept = (day) => {
    if (day < anchorDay) return 'skip'; // BYDAY days before the series start
    if (untilDay && day > untilDay) return 'stop'; // UNTIL inclusive
    if (day >= generateUntil) return 'stop';
    out.push(day);
    if (rule.count != null && out.length >= rule.count) return 'stop';
    return 'ok';
  };

  if (rule.freq === 'daily') {
    const k0 = fastForward(
      wsDay ? Math.round((wsDay - anchorDay) / 86_400_000) : 0
    );
    for (let k = k0; steps < MAX_ITERATIONS; k += 1, steps += 1) {
      const verdict = accept(addDays(anchorDay, k * interval));
      if (verdict === 'stop') break;
      if (steps === MAX_ITERATIONS - 1) capTripped = true;
    }
    return finish(out, capTripped, master);
  }

  if (rule.freq === 'weekly') {
    const codes = (rule.by_day && rule.by_day.length > 0
      ? rule.by_day.slice()
      : [byDayCodeForDate(anchorDay)]
    ).sort((a, b) => MONDAY_OFFSET[a] - MONDAY_OFFSET[b]);
    const week0 = startOfWeek(anchorDay); // Monday-based, the app-wide convention
    const w0 = fastForward(
      wsDay ? Math.floor(Math.round((startOfWeek(wsDay) - week0) / 86_400_000) / 7) : 0
    );
    outer: for (let w = w0; steps < MAX_ITERATIONS; w += 1) {
      const weekStart = addDays(week0, w * interval * 7);
      for (const code of codes) {
        steps += 1;
        const verdict = accept(addDays(weekStart, MONDAY_OFFSET[code]));
        if (verdict === 'stop') break outer;
        if (steps >= MAX_ITERATIONS) {
          capTripped = true;
          break outer;
        }
      }
    }
    return finish(out, capTripped, master);
  }

  // monthly — day-of-month iteration with the RFC skip rule.
  const dayOfMonth = anchorDay.getDate();
  const k0 = fastForward(
    wsDay
      ? (wsDay.getFullYear() - anchorDay.getFullYear()) * 12 +
          (wsDay.getMonth() - anchorDay.getMonth())
      : 0
  );
  for (let k = k0; steps < MAX_ITERATIONS; k += 1, steps += 1) {
    const year = anchorDay.getFullYear();
    const monthIndex = anchorDay.getMonth() + k * interval;
    const y = year + Math.floor(monthIndex / 12);
    const m = ((monthIndex % 12) + 12) % 12;
    if (daysInMonth(y, m) < dayOfMonth) continue; // SKIP, never clamp; consumes no COUNT
    const verdict = accept(new Date(y, m, dayOfMonth));
    if (verdict === 'stop') break;
    if (steps === MAX_ITERATIONS - 1) capTripped = true;
  }
  return finish(out, capTripped, master);
}

/** Cap backstop: render what we have AND say so once — never blank silently. */
function finish(out, capTripped, master) {
  if (capTripped) {
    // eslint-disable-next-line no-console
    console.warn(
      `[calendar] recurrence expansion for ${master.uid} hit the ${MAX_ITERATIONS}-step cap — rendering the ${out.length} generated occurrences`
    );
  }
  return out;
}

/** Concrete instance for a master on a local day (wall clock re-applied). */
function instanceOn(master, day) {
  const occurrenceDate = toDateKey(day);
  const base = { ...master, occurrence_date: occurrenceDate, is_occurrence: true };
  if (master.all_day) {
    const startKey = fromDateKey(master.start_date);
    const endKey = fromDateKey(master.end_date || master.start_date);
    const spanDays = Math.round((startOfDay(endKey) - startOfDay(startKey)) / 86_400_000);
    return {
      ...base,
      start_date: occurrenceDate,
      end_date: toDateKey(addDays(day, Math.max(spanDays, 0))),
    };
  }
  const anchor = anchorStartOf(master);
  const durationMs = Math.max(new Date(master.end) - new Date(master.start), 0);
  const start = dayAtMinutes(day, minutesOfDay(anchor)); // wall clock held (DST-safe)
  return {
    ...base,
    start: start.toISOString(),
    end: new Date(start.getTime() + durationMs).toISOString(),
  };
}

/**
 * Expand a mixed event list for a window: singles and override records pass
 * through untouched; recurring masters are replaced by their instances in
 * [windowStart, windowEnd), with instances suppressed when exdated or
 * overridden. No order is promised across masters.
 */
export function expandEvents(events, windowStart, windowEnd) {
  const winStart = startOfDay(windowStart);
  const winEnd = windowEnd;
  const out = [];
  // Overrides indexed by master + occurrence for suppression.
  const overridden = new Set();
  for (const e of events || []) {
    if (e?.override_of?.master_uid && e.override_of.occurrence_date) {
      overridden.add(`${e.override_of.master_uid}:${e.override_of.occurrence_date}`);
    }
  }
  for (const event of events || []) {
    if (!event) continue;
    if (!event.recurrence) {
      out.push(event); // singles + overrides as themselves
      continue;
    }
    const problem = recurrenceProblem(event.recurrence);
    if (problem) {
      // eslint-disable-next-line no-console
      console.warn(`[calendar] malformed recurrence on ${event.uid} (${problem}) — treated as single`);
      out.push({ ...event, recurrence: null });
      continue;
    }
    const exdates = new Set(Array.isArray(event.exdates) ? event.exdates : []);
    for (const day of occurrenceDays(event, winEnd, winStart)) {
      const key = toDateKey(day);
      if (exdates.has(key)) continue; // cancelled instance
      if (overridden.has(`${event.uid}:${key}`)) continue; // replaced instance
      const instance = instanceOn(event, day);
      // Window intersection AFTER generation (never iterate from the window —
      // anchoring) — cheap since generation stops at winEnd anyway.
      const instStart = event.all_day
        ? fromDateKey(instance.start_date)
        : new Date(instance.start);
      const instEnd = event.all_day
        ? addDays(fromDateKey(instance.end_date), 1)
        : new Date(instance.end);
      if (instEnd <= winStart || instStart >= winEnd) continue;
      out.push(instance);
    }
  }
  return out;
}
